import { npcs } from '../data/guide/npcs'
import { entityImages } from '../data/guide/entityImages'
import { npcSheetIds } from '../data/guide/npcPhotoSheets'
import { npcIdentities } from '../data/guide/npcIdentity'
import type { Monster, WorldData } from '../types/world'

/**
 * 06_monsters.csv (como el Monster.txt del servidor) mezcla monstruos, NPC y objetos en la misma tabla.
 * La guía los separa así, sin tocar los archivos:
 * - NPC de la guía (data/guide/npcs) o marcado como NPC por el staff en las imágenes.
 * - Rotulado como NPC en las láminas VANGUARD_NPC (data/guide/npcPhotoSheets).
 * - NPC que entrega misiones (npc_monster_id en quest_rules).
 * Todo lo demás se trata como monstruo/objeto del catálogo. No se usa el nivel 0, la falta de foto, de spot
 * permanente ni un umbral de ID para decidir que algo es NPC (p. ej. Spider Eggs 460–462 siguen como entidades del catálogo).
 * Tener o no foto nunca oculta una entidad: sin imagen se muestra con marcador neutro.
 */
export type MonsterKind = 'monstruo' | 'npc'

const cache = new WeakMap<WorldData, Set<number>>()

function npcIds(w: WorldData): Set<number> {
  const hit = cache.get(w)
  if (hit) return hit
  const ids = new Set<number>()
  npcs.forEach((n) => n.monsterId !== undefined && ids.add(n.monsterId))
  // Override de clasificación: IDs rotulados como NPC en las láminas VANGUARD_NPC.
  npcSheetIds.forEach((id) => ids.add(id))
  entityImages.forEach((e) => e.kind === 'npc' && e.monsterId !== undefined && ids.add(e.monsterId))
  w.quests?.rules.forEach((r) => r.npcMonsterId !== undefined && ids.add(r.npcMonsterId))
  // Identidades de NPC indicadas por el staff (data/guide/npcIdentity), incluidas las unificadas con otro ID.
  npcIdentities.forEach((n) => ids.add(n.id))
  cache.set(w, ids)
  return ids
}

export function monsterKind(w: WorldData, m: Monster): MonsterKind {
  return npcIds(w).has(m.id) ? 'npc' : 'monstruo'
}
